// TreeConnector — stateless SVG `<path>` joining a parent's bottom-centre to a
// child's top-centre with a smooth cubic Bezier.
//
// The control points are placed halfway down the vertical gap so the curve
// flows naturally between rows. Path direction is implicit: source is the
// manager (parent), target is the report (child).

interface TreeConnectorProps {
  /** Parent (manager) bottom-centre x. */
  sourceX: number;
  /** Parent (manager) bottom-centre y. */
  sourceY: number;
  /** Child (report) top-centre x. */
  targetX: number;
  /** Child (report) top-centre y. */
  targetY: number;
  /** When true the line is dashed — used for ghost-manager (outside-export)
   *  reporting lines. */
  dashed?: boolean;
}

export function TreeConnector({
  sourceX,
  sourceY,
  targetX,
  targetY,
  dashed = false,
}: Readonly<TreeConnectorProps>) {
  // Cubic Bezier with control points placed halfway down the vertical gap.
  // The horizontal alignment of the control points matches each endpoint so
  // the curve leaves the parent straight downward and enters the child
  // straight upward.
  const midY = (sourceY + targetY) / 2;
  const path = `M ${sourceX},${sourceY} C ${sourceX},${midY} ${targetX},${midY} ${targetX},${targetY}`;

  return (
    <path
      d={path}
      fill="none"
      stroke="var(--color-border)"
      strokeOpacity={0.7}
      strokeWidth={1.5}
      strokeDasharray={dashed ? '5 4' : undefined}
      data-dashed={dashed ? 'true' : undefined}
      data-testid="tree-connector"
      className="tree-connector-anim"
    />
  );
}
